import type { GalleryImage, GalleryResponse } from '../types/gallery.types.js';

export interface RoundtripGalleryMiddlewareRequest {
    brand: string;
    tenant: string;
    lang: string;
    currency: string;
    identifier: string;
    travelcontext?: string;
}

function resolveBaseUrl(): string {
    return import.meta.env.VITE_ROUNDTRIP_GALLERY_SERVICE_URL ?? 'http://localhost:7071/api/roundtrip-gallery';
}

function buildRoundtripGalleryMiddlewareUrl(request: RoundtripGalleryMiddlewareRequest): URL {
    const url = new URL(resolveBaseUrl());

    Object.entries(request).forEach(([key, value]) => {
        if (typeof value === 'string' && value.trim().length > 0) {
            url.searchParams.set(key, value);
        }
    });

    return url;
}

function normalizeImages(images: unknown): GalleryImage[] {
    if (!Array.isArray(images)) {
        return [];
    }

    return images
        .map((raw) => {
            if (!raw || typeof raw !== 'object') {
                return null;
            }

            const image = raw as {
                title?: unknown;
                altText?: unknown;
                text?: unknown;
                url?: unknown;
                size?: unknown;
                aspectRatio?: unknown;
            };

            const title = typeof image.title === 'string' ? image.title : '';
            const text =
                typeof image.text === 'string' ? image.text : typeof image.altText === 'string' ? image.altText : title;
            const url = typeof image.url === 'string' ? image.url : '';
            const size =
                image.size === 'ORIGINAL' ||
                image.size === 'DESKTOP' ||
                image.size === 'TABLET' ||
                image.size === 'MOBILE'
                    ? image.size
                    : null;

            if (!url || !size) {
                return null;
            }

            return {
                title,
                text,
                url,
                size,
                aspectRatio:
                    typeof image.aspectRatio === 'number'
                        ? String(image.aspectRatio)
                        : typeof image.aspectRatio === 'string'
                          ? image.aspectRatio
                          : undefined,
            } as GalleryImage;
        })
        .filter((image): image is GalleryImage => Boolean(image));
}

/**
 * Requests gallery image data from middleware and normalizes the response shape.
 * @param request Middleware query parameters used to load gallery content.
 * @returns Normalized gallery response with validated image entries.
 */
export async function fetchRoundtripGalleryContent(
    request: RoundtripGalleryMiddlewareRequest
): Promise<GalleryResponse> {
    const url = buildRoundtripGalleryMiddlewareUrl(request);
    console.log('[roundtrip-gallery] Fetching middleware data', {
        url: url.toString(),
        request,
    });
    const response = await fetch(url.toString());

    if (!response.ok) {
        throw new Error(`Failed to fetch roundtrip-gallery content: ${response.status}`);
    }

    const payload = (await response.json()) as {
        images?: unknown;
        meta?: GalleryResponse['meta'];
        data?: {
            images?: unknown;
            meta?: GalleryResponse['meta'];
        };
    };

    const images = payload.data?.images ?? payload.images;
    const meta = payload.data?.meta ?? payload.meta;

    return {
        images: normalizeImages(images),
        meta,
    };
}
